import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Trash2, Pen, Upload, AlertTriangle } from 'lucide-react';

/**
 * Lightweight canvas-based signature pad with a second "Upload" mode.
 * Outputs a base64 PNG via onChange.
 *
 *   Mode: 'draw'   — original canvas pen (mouse/touch/stylus)
 *   Mode: 'upload' — image file picker (PNG / JPG); we render the image
 *                    onto a canvas so the consumer always gets a normalised
 *                    PNG data URL, regardless of upload format.
 *
 * Both modes share the same onChange contract — consumers don't need to
 * know which the customer chose.
 */
export const SignaturePad = ({ onChange, height = 180, dataTestId = 'signature-pad' }) => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState('draw'); // 'draw' | 'upload'
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadFilename, setUploadFilename] = useState('');

  const getCtx = () => canvasRef.current?.getContext('2d');

  const sizeCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';
  };

  useEffect(() => {
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);
    return () => window.removeEventListener('resize', sizeCanvas);
  }, []);

  // Switching mode wipes whatever's on the canvas so the customer can't
  // accidentally submit a stale signature from the prior mode.
  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    clear();
    setMode(nextMode);
    setUploadError('');
    setUploadFilename('');
  };

  // ---- Draw mode ---------------------------------------------------------
  const pointer = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    if (mode !== 'draw') return;
    e.preventDefault();
    const ctx = getCtx(); if (!ctx) return;
    const { x, y } = pointer(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing || mode !== 'draw') return;
    e.preventDefault();
    const ctx = getCtx(); if (!ctx) return;
    const { x, y } = pointer(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    if (onChange && canvasRef.current) {
      onChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  // ---- Upload mode -------------------------------------------------------
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    setUploadError('');
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) {
      setUploadError('Please upload a PNG, JPG, or WEBP image of your signature');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Signature image must be 5MB or smaller');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Paint the uploaded image onto the same canvas (fit, centered) so
        // consumers receive a uniform PNG data URL regardless of source.
        const c = canvasRef.current;
        if (!c) return;
        sizeCanvas();
        const ctx = getCtx();
        if (!ctx) return;
        ctx.clearRect(0, 0, c.width, c.height);
        const rect = c.getBoundingClientRect();
        const cw = rect.width, ch = rect.height;
        const scale = Math.min(cw / img.width, ch / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (cw - w) / 2;
        const y = (ch - h) / 2;
        ctx.drawImage(img, x, y, w, h);
        setHasInk(true);
        setUploadFilename(file.name);
        if (onChange) onChange(c.toDataURL('image/png'));
      };
      img.onerror = () => setUploadError('Could not read that image — please try a different file');
      img.src = reader.result;
    };
    reader.onerror = () => setUploadError('Could not read that file');
    reader.readAsDataURL(file);
  };

  const clear = () => {
    const ctx = getCtx();
    if (!ctx) return;
    const c = canvasRef.current;
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    setUploadFilename('');
    setUploadError('');
    if (onChange) onChange('');
  };

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      {/* Mode switcher — same visual weight for both options so customers
          know upload is a first-class alternative, not a hidden escape hatch. */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5 w-fit text-xs" data-testid={`${dataTestId}-mode-switcher`}>
        <button
          type="button"
          onClick={() => switchMode('draw')}
          className={`flex items-center gap-1 px-2.5 py-1 rounded font-medium transition-colors ${mode === 'draw' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid={`${dataTestId}-mode-draw`}
        >
          <Pen className="w-3 h-3" /> Draw
        </button>
        <button
          type="button"
          onClick={() => switchMode('upload')}
          className={`flex items-center gap-1 px-2.5 py-1 rounded font-medium transition-colors ${mode === 'upload' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid={`${dataTestId}-mode-upload`}
        >
          <Upload className="w-3 h-3" /> Upload image
        </button>
      </div>

      <div className="border border-dashed border-slate-300 rounded-lg bg-white relative">
        <canvas
          ref={canvasRef}
          className={`w-full block touch-none ${mode === 'draw' ? 'cursor-crosshair' : 'cursor-default'}`}
          style={{ height: `${height}px` }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {mode === 'upload' && !hasInk && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-blue-700 hover:bg-blue-50/40 transition-colors rounded-lg"
            data-testid={`${dataTestId}-upload-zone`}
          >
            <Upload className="w-6 h-6" />
            <span className="text-xs font-medium">Click to upload your signature image</span>
            <span className="text-[10px] text-slate-400">PNG, JPG or WEBP · max 5MB</span>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleUpload}
          className="hidden"
          data-testid={`${dataTestId}-upload-input`}
        />
      </div>

      {uploadError && (
        <div className="flex items-center gap-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
          <AlertTriangle className="w-3 h-3" /> {uploadError}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500">
          {hasInk
            ? (mode === 'upload' && uploadFilename ? `Uploaded: ${uploadFilename}` : 'Signed')
            : mode === 'draw'
              ? 'Draw your signature above with your finger, stylus, or mouse'
              : 'Upload an image of your existing signature'}
        </p>
        <Button size="sm" variant="ghost" onClick={clear} disabled={!hasInk} data-testid={`${dataTestId}-clear`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default SignaturePad;
